import { useState, useEffect } from 'react';
import apiClient from '@/core/apiClient';

export interface VatRegister {
  id: number;
  vat_type: 'SOPORTADA' | 'REPERCUTIDA';
  vat_amount: number;
  base_amount: number;
  vat_percentage: number;
  invoice_number: string;
  invoice_date: string;
  supplier_name: string;
  nif_cif: string;
  created_at: string;
}

export interface VatSummary {
  soportada_total: number;
  repercutida_total: number;
  neto: number;
  year: number;
}

export function VatManagement() {
  const [vats, setVats] = useState<VatRegister[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'SOPORTADA' | 'REPERCUTIDA'>('SOPORTADA');
  const [summary, setSummary] = useState<VatSummary | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadVats();
    loadSummary();
  }, [activeTab, year]);

  const loadVats = async () => {
    setLoading(true);
    try {
      const endpoint = activeTab === 'SOPORTADA' ? '/comptabilitat/vat/soportada' : '/comptabilitat/vat/repercutida';
      const response = await apiClient.get(endpoint);
      setVats(response.data?.data || []);
    } catch (error) {
      console.error('[v0] Error loading VATs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await apiClient.get(`/comptabilitat/vat/summary/${year}`);
      setSummary(response.data?.data);
    } catch (error) {
      console.error('[v0] Error loading summary:', error);
    }
  };

  const downloadModelo347 = async () => {
    try {
      const response = await apiClient.get(`/comptabilitat/vat/exports/modelo347/${year}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `modelo347_${year}.csv`;
      a.click();
    } catch (error) {
      console.error('[v0] Error downloading Modelo 347:', error);
      alert('Error en descarregar el Model 347');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Gestió de l&apos;IVA</h1>
        <p className="text-gray-600">Registra i gestiona el teu IVA suportat i repercutit</p>
      </div>

      <div className="bg-white p-4 rounded border space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="font-bold">Resum IVA {year}</h2>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="px-3 py-2 border rounded w-24"
          />
        </div>

        {summary && (
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-blue-50 rounded">
              <div className="text-xs text-gray-600">IVA Suportat</div>
              <div className="text-2xl font-bold text-blue-600">
                {(summary?.soportada_total || 0).toLocaleString('ca-ES', {
                  minimumFractionDigits: 2,
                })}
                €
              </div>
            </div>
            <div className="p-3 bg-green-50 rounded">
              <div className="text-xs text-gray-600">IVA Repercutit</div>
              <div className="text-2xl font-bold text-green-600">
                {(summary?.repercutida_total || 0).toLocaleString('ca-ES', {
                  minimumFractionDigits: 2,
                })}
                €
              </div>
            </div>
            <div className="p-3 bg-orange-50 rounded">
              <div className="text-xs text-gray-600">Net a Pagar</div>
              <div className="text-2xl font-bold text-orange-600">
                {(summary?.neto || 0).toLocaleString('ca-ES', {
                  minimumFractionDigits: 2,
                })}
                €
              </div>
            </div>
          </div>
        )}

        <button
          onClick={downloadModelo347}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Descarregar Model 347 ({year})
        </button>
      </div>

      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('SOPORTADA')}
          className={`px-4 py-2 font-bold ${
            activeTab === 'SOPORTADA'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600'
          }`}
        >
          IVA Suportat
        </button>
        <button
          onClick={() => setActiveTab('REPERCUTIDA')}
          className={`px-4 py-2 font-bold ${
            activeTab === 'REPERCUTIDA'
              ? 'border-b-2 border-green-600 text-green-600'
              : 'text-gray-600'
          }`}
        >
          IVA Repercutit
        </button>
      </div>

      {loading && <p className="text-gray-600">Carregant...</p>}

      {vats.length === 0 ? (
        <p className="text-gray-500">No hi ha registres d&apos;IVA</p>
      ) : (
        <div className="space-y-3">
          {vats.map((vat) => (
            <div key={vat.id} className="border p-4 rounded hover:bg-gray-50">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-bold">{vat.supplier_name || 'Sense proveïdor'}</div>
                  <div className="text-xs text-gray-500">Factura: {vat.invoice_number}</div>
                  <div className="text-xs text-gray-500">NIF: {vat.nif_cif}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{vat.invoice_date}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Base: </span>
                  <span className="font-bold">
                    {vat.base_amount.toLocaleString('ca-ES', {
                      minimumFractionDigits: 2,
                    })}
                    €
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">% IVA: </span>
                  <span className="font-bold">{vat.vat_percentage}%</span>
                </div>
                <div>
                  <span className="text-gray-600">Quota: </span>
                  <span className="font-bold text-blue-600">
                    {vat.vat_amount.toLocaleString('ca-ES', {
                      minimumFractionDigits: 2,
                    })}
                    €
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
